import React, { useEffect, useState } from 'react';
import { ComposedChart, Line, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, ResponsiveContainer, Label, Tooltip, Legend, ScatterChart, Dot } from 'recharts';
import { MeasureInfoType, GraphDataInfoType, SelectDataType, IsTemperatureItem, GraphDataType, XYDiagramSettingModel, XYDiagramDateSettingModel } from './XYDiagram';
import * as Constant from '../../../Constant';
import { makeStyles, createStyles } from '@material-ui/core/styles';
const isDispLog: boolean = true;

export type GraphDispSetting = {
  yMin: number;
  yMax: number;
  xMin: number;
  xMax: number;
  isPileup: boolean;
};

type GraphData = {
  lineNo: number;
  lineName: string;
  points: GraphPoint[];
  valueUpper: number;
  valueUpperLimit: number;
  selectFlg: boolean;
  infoColor: string;
};
type GraphPoint = {
  xValue: number;
  yValue: number;
  regressionValue: number;
  valueColDate: string;
};

type GraphSetting = {
  upper: number;
  upperLimit: number;
  yMin: number;
  yMax: number;
  xMin: number;
  xMax: number;
  backColor: string;
  memoryFontColor: string;
  yUnit: string;
  xUnit: string;
  yUnitColor: string;
  xUnitColor: string;
  gridColor: string;
};

type Props = {
  selectData: SelectDataType;
  mesInfoList: MeasureInfoType[];
  update: string;
  groupNo: number;
  updateLoadginState: (value: boolean) => void;
  chartContainerStyle: string;
  xyDiagramSettingModel: XYDiagramSettingModel;
};

const useStyles = makeStyles(
  createStyles({
    customTooltip: {
      background: '#FFFFEE',
      fontSize: '14px',
      padding: '10px 10px 0px 20px',
    },
  })
);

const UpDateGraphData = (data: GraphDataInfoType): GraphData => {
  var item: GraphData = {
    lineNo: 1,
    valueUpper: 0,
    valueUpperLimit: 0,
    selectFlg: true,
    points: [],
    infoColor: 'red',
    lineName: '1',
  };

  for (let i = 0; i < data.GraphDatas.length; i++) {
    item.points.push({
      xValue: data.GraphDatas[i].XValue,
      yValue: data.GraphDatas[i].YValue,
      regressionValue: data.GraphDatas[i].RegressionValue,
      valueColDate: data.GraphDatas[i].ValueColDate
    });
  }
  return item;
};

const XYDiagramChart: React.FC<Props> = (props) => {
  //console.debug(['XYDiagramChart()', props]);
  const classes = useStyles();

  const [graphData, setGraphData] = useState<GraphData>();

  const [graphDataInfoType, setGraphDataInfoType] = useState<GraphDataInfoType>({ GraphDatas: [] });
  const [xyDiagramSettingModel, setXyDiagramSettingModel] = useState<XYDiagramSettingModel>(props.xyDiagramSettingModel);

  const [graphSetting, setGraphSetting] = useState<GraphSetting>({
    upper: 0,
    upperLimit: 0,
    yMax: 0,
    yMin: 0,
    xMax: 0,
    xMin: 0,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    yUnit: '',
    xUnit: '',
    yUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    xUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
  });

  //表示ボタン押下時
  useEffect(() => {
    if (props.update === "") {
      console.debug("update no use");
      return;
    }
    console.debug(['useEffect GetXYDiagramModel Start']);
    setXyDiagramSettingModel(props.xyDiagramSettingModel)

    var x = props.mesInfoList.find((item) => item.id === props.selectData.xid);
    var y = props.mesInfoList.find((item) => item.id === props.selectData.yid);
    graphSetting.xUnit = x?.Unit ?? '';
    graphSetting.yUnit = y?.Unit ?? '';

    if (IsTemperatureItem(x)) {
      //温度のデータの場合
      graphSetting.xMax = 150;
      graphSetting.xMin = 0;
    } else {
      graphSetting.xMax = 0;
      graphSetting.xMin = 0;
    }

    if (IsTemperatureItem(y)) {
      //温度のデータの場合
      graphSetting.yMax = 150;
      graphSetting.yMin = 0;
    } else {
      graphSetting.yMax = 0;
      graphSetting.yMin = 0;
    }
    //グラフデータ目盛り設定
    console.debug(["useEffect XYDiagramChart", props.xyDiagramSettingModel]);

    //グラフの軸範囲を取得
    //複数のラインデータから最小値と最大値を収集
    let ymin = 0;
    let ymax = 0;
    let xmin = 0;
    let xmax = 0;
    props.xyDiagramSettingModel.GraphDataItems.forEach((r) => {
      // console.debug(["useEffect XYDiagramChart row", r]);
      let graphDataInfoType: GraphDataInfoType = { GraphDatas: r.GraphDatas };
      let items = updateGraphDomain(graphDataInfoType, ymin, ymax, xmin, xmax);
      ymin = items[0];
      ymax = items[1];
      xmin = items[2];
      xmax = items[3];
    })
    //console.debug(['gettemp vaues', ymin, ymin, xmin, xmax])
    //ラインデータ取得
    graphSetting.yMin = ymin;
    graphSetting.yMax = ymax;
    graphSetting.xMin = xmin;
    graphSetting.xMax = xmax;
    setGraphSetting({ ...graphSetting });
    setGraphData(UpDateGraphData(graphDataInfoType));

    props.updateLoadginState(false);
    console.debug(['useEffect GetXYDiagramModel catch']);

  }, [props.update]);

  /**
   * 目盛りの最大値と最小値を調整します。
   * @param max
   * @param min
   * @returns
   */
  const adjustValue = (max: number, min: number, axisType: string): [Max: number, Min: number] => {
    //console.debug(['max, min', max, min]);
    let diff = max - min;
    if (diff === 0) {
      max = 10;
      min = -10;
      diff = 20;
    }

    const logValue = Math.pow(10, Math.floor(Math.log10(Math.abs(diff / 10)))) * 5;
    console.debug(['adjustValue diff', diff, logValue]);

    // 差分値により調整します。
    const maxBaseValue = Math.round((max - (max % logValue)) / logValue);
    const minBaseValue = Math.round((min - (min % logValue)) / logValue);
    let maxValue = 0.0;
    let minValue = 0.0;

    for (let ii = 0; ii <= 10; ii++) {
      if (max <= (maxBaseValue + ii - 1) * logValue) {
        let xyMaxValue = (maxBaseValue + ii - 1) * logValue;
        maxValue = Number(xyMaxValue.toFixed(3));
        break;
      }
    }

    for (let ii = 0; ii <= 10; ii++) {
      if (min >= (minBaseValue - ii + 1) * logValue) {
        minValue = (minBaseValue - ii + 1) * logValue;
        break;
      }
    }
    //console.debug(maxBaseValue, minBaseValue, maxValue, minValue);
    return [maxValue, minValue];
  };

  /**
   * グラフデータ目盛り設定
   * @param gData
   */
  const updateGraphDomain = (gData: GraphDataInfoType, tempYmin: number, tempYmax: number, tempXmin: number, tempXmax: number):
    [tempYmin: number, tempYmax: number, tempXmin: number, tempXmax: number] => {
    console.debug(['updateGraphDomain Data', gData, tempYmin, tempYmax, tempXmin, tempXmax]);
    if (gData.GraphDatas === null) {
      return [0, 0, 0, 0];
    }
    if (gData.GraphDatas.length === 1) {
      const yMin = gData.GraphDatas[0].YValue - 10;
      const yMax = gData.GraphDatas[0].YValue + 10;
      const xMin = gData.GraphDatas[0].XValue - 10;
      const xMax = gData.GraphDatas[0].XValue + 10;

      const adjustValueY = adjustValue(yMax, yMin, 'y');
      graphSetting.yMax = adjustValueY[0];
      graphSetting.yMin = adjustValueY[1];

      const adjustValueX = adjustValue(xMax, xMin, 'X');
      graphSetting.xMax = adjustValueX[0];
      graphSetting.xMin = adjustValueX[1];

      console.debug(['- データが1点時、XYメモリ設定 -', graphSetting]);
      return [graphSetting.yMin, graphSetting.yMax, graphSetting.xMin, graphSetting.xMax]
    } else {
      let xmin = graphSetting.xMin;
      let xmax = graphSetting.xMax;
      let ymin = graphSetting.yMin;
      let ymax = graphSetting.yMax;

      //Y軸
      let maxValue = Math.max(...gData.GraphDatas?.map((p) => p.YValue))
      let minValue = Math.min(...gData.GraphDatas?.map((p) => p.YValue))
      minValue = (minValue > tempYmin || minValue < 0) ? tempYmin : minValue;
      maxValue = maxValue <= tempYmax ? tempYmax : maxValue;

      const adjustValueY = adjustValue(
        maxValue,
        minValue,
        'y'
      );
      console.debug(['adjustValueY', adjustValueY]);
      if (adjustValueY[0] > adjustValueY[1]) {
        // TODO 2022年 未対応
        // 温度：0～150
        // 電流：0～Auto

        ymax = graphSetting.yMax < adjustValueY[0] ? adjustValueY[0] : graphSetting.yMax;
        graphSetting.yMax = ymax;
        ymin = graphSetting.yMax > 0 ? 0 : adjustValueY[1];
        graphSetting.yMin = ymin;

        //X軸
        let maxValue = Math.max(...gData.GraphDatas?.map((p) => p.XValue))
        let minValue = Math.min(...gData.GraphDatas?.map((p) => p.XValue))
        minValue = (minValue > tempXmin || minValue < 0) ? tempXmin : minValue;
        maxValue = maxValue <= tempXmax ? tempXmax : maxValue;

        const adjustValueX = adjustValue(
          Math.max(maxValue),
          Math.min(minValue),
          'x'
        );
        console.debug(['adjustValueX', adjustValueX]);
        // TODO 2022年 未対応
        // 温度：0～150
        // 電流：0～Auto

        xmax = graphSetting.xMax < adjustValueX[0] ? adjustValueX[0] : graphSetting.xMax;
        graphSetting.xMax = xmax;
        xmin = graphSetting.xMax > 0 ? 0 : adjustValueX[1];
        graphSetting.xMin = xmin;

        // console.debug(['change graphSetting', graphSetting])
      }
      return [ymin, ymax, xmin, xmax]
    }
  };

  /**
   * ツールチップ表示
   * @param param0
   * @returns
   */
  const CustomTooltip = ({ active, payload, label }: any) => {
    console.debug(['CustomTooltip payload', payload, label]);
    if (active) {
      return (
        <div className={classes.customTooltip}>
          {xyDiagramSettingModel.GraphDataItems.map((r: any, index: any) => {
            console.debug(['xyDiagramSettingModel.GraphDataItems', r]);
            let pointDate: string = "";
            if (r.GraphDatas !== null) {
              r.GraphDatas.map((rr: any, index2: any) => {
                //console.debug(['xyDiagramSettingModel.GraphDataItems[].GraphDatas', rr]);
                //payloadと一致する場合、日付を取得
                if (rr.XValue === payload[0].payload.xValue && rr.YValue === payload[0].payload.yValue && rr.ValueColDate === payload[0].payload.valueColDate) {
                  //console.debug(['CustomTooltip index', index, payload[0]]);
                  return pointDate = rr.ValueColDate;
                }
              })
            }

            //日付が取得できていたらツールチップ表示する
            if (pointDate !== "") {
              return (
                <div style={{ color: r.ColorDataSetting.ScatterColor }}>
                  {r.YearDateSetting.SymbolName} : {(pointDate)}
                </div>)
            }
          })}
        </div>
      );
    }
    return null;
  };

  /**
   * 記号取得
   * @param symbolName
   * @returns
   */
  const getSymbol = (symbolName: string): any => {
    if (symbolName === "▲") {
      return "triangle";
    }
    if (symbolName === "■") {
      return "square";
    }
    if (symbolName === "♦") {
      return "diamond";
    }
    if (symbolName === "★") {
      return "star";
    }
    return "circle";

  }

  /**
   * ラインデータ取得
   * @returns
   */
  const getLineData = (): any => {
    let graphPoint: GraphPoint[] = []
    let tempGraphPoint: GraphPoint[] = []
    xyDiagramSettingModel.GraphDataItems.map((row, index) => {
      if (row.GraphDatas !== null) {
        if (Object.keys(row.GraphDatas).length !== 0) {
          row.GraphDatas.map((r) => {
            tempGraphPoint.push({
              xValue: r.XValue,
              yValue: r.RegressionValue,  //分布図(Scatter)ではregressionValueが使用できなかったのでyValueに代入する
              regressionValue: r.RegressionValue,
              valueColDate: r.ValueColDate
            })
          })
        }
      }
    })
    //console.debug(['getLineData regressionValue', tempGraphPoint]);
    let xValueList: number[] = []
    tempGraphPoint.map((r) => {
      xValueList.push(r.xValue)
    })
    if (xValueList !== undefined) {
      let tempMin = Math.min(...xValueList);
      let tempMax = Math.max(...xValueList);
      //X軸の最小値を取得
      let temp = tempGraphPoint.find((r) => r.xValue === tempMin);
      if (temp !== undefined) {
        //console.debug(['getLineData xMin value', temp]);
        graphPoint.push(temp)
      }
      //X軸の最大値を取得
      temp = tempGraphPoint.find((r) => r.xValue === tempMax);
      if (temp !== undefined) {
        //console.debug(['getLineData xMax value', temp]);
        graphPoint.push(temp)
      }
    }
    console.debug(['getLineData regressionValue', graphPoint]);
    return graphPoint
  }

  /**
   * 分布図データ取得
   * @param item
   * @returns
   */
  const getDistributionMapData = (item: Array<GraphDataType>): any => {
    let graphPoint: GraphPoint[] = []
    item.map((row, index) => {
      graphPoint.push({
        xValue: row.XValue,
        yValue: row.YValue,
        regressionValue: row.RegressionValue,
        valueColDate: row.ValueColDate
      })
    })
    return graphPoint;
  }
  /**
   * ライン側のドットサイズ変更処理
   * @param param0 
   * @returns 
   */
  const RenderDot: React.FC<{ cx: number, cy: number }> = ({ cx, cy }) => {
    return (
      <Dot cx={cx} cy={cy} fill="blue" r={1} />
    )
  }

  //console.debug(['- 画面描画開始 -', graphSetting, props]);
  return (
    <ResponsiveContainer className={props.chartContainerStyle} width="99%">
      <ScatterChart width={500} height={300} data={graphData?.points} style={{ background: graphSetting.backColor }}>
        <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="4 4" />
        <XAxis
          dataKey="xValue"
          type="number"
          allowDuplicatedCategory={true}
          allowDataOverflow={true}
          domain={[graphSetting.xMin, graphSetting.xMax]}
          stroke={graphSetting.memoryFontColor}
          height={50}
          tickCount={5}
          tick={{ dy: 10 }}
          padding={{ right: 0, left: 1 }}
        >
          <Label angle={0} position="insideBottom" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.xUnit}
          </Label>
        </XAxis>
        <YAxis
          width={85}
          dataKey="yValue"
          type="number"
          tickCount={10}
          allowDataOverflow={true}
          stroke={graphSetting.memoryFontColor}
          domain={[graphSetting.yMin, graphSetting.yMax]}
        >
          <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.yUnit}
          </Label>
        </YAxis>

        {/**ツールチップ表示 */}
        <Tooltip content={<CustomTooltip />} />

        {/**ライン表示 */}
        <Scatter dataKey="regressionValue" data={getLineData()} fill="blue" line={{ strokeWidth: 2 }} shape={<RenderDot cx={1} cy={1} />} />

        {/**分布図表示 */}
        {xyDiagramSettingModel.GraphDataItems.map((row, index) => {
          console.debug(["GraphDataItems row", row, index]);
          if (row.GraphDatas !== null) {
            //if (Object.keys(row.GraphDatas).length !== 0) {
            //console.debug(['xyDiagramSettingModel row item', row]);
            return (
              <Scatter
                name={row.YearDateSetting.SymbolName}
                data={getDistributionMapData(row.GraphDatas)}
                dataKey="yValue"
                fill={row.ColorDataSetting.ScatterColor}
                shape={getSymbol(row.YearDateSetting.SymbolName)}
              />
            )
            //}
          }
        })}
      </ScatterChart>
    </ResponsiveContainer>
  );
};
export default XYDiagramChart;
